import { type FieldValues, useController } from 'react-hook-form';

import {
  DropdownSingleSelectAsync,
  type DropdownSingleSelectAsyncProps,
} from '~/chords/DropdownSingleSelectAsync/DropdownSingleSelectAsync';
import { FormField, type FormFieldProps } from '~/chords/FormField';
import { MultiSelect } from '~/chords/MultiSelect/MultiSelect';

type FormFieldDropdownMultiSelectAsyncProps<
  T,
  K,
  TFormFields extends FieldValues,
> = Omit<
  FormFieldProps<TFormFields>,
  | 'children'
  | 'dangerouslyOverrideClassName'
  | 'triggerClassName'
  | 'fieldMessageClassName'
  | 'labelAsSpan'
> &
  Omit<
    DropdownSingleSelectAsyncProps<T, K>,
    'onChange' | 'defaultValue' | 'resolveFn' | 'defaultValueErrorMessage'
  > & {
    onValueChange?: (items: T[]) => void;
  };

export function FormFieldDropdownMultiSelectAsync<
  T,
  K,
  TFormFields extends FieldValues = FieldValues,
>(props: FormFieldDropdownMultiSelectAsyncProps<T, K, TFormFields>) {
  const {
    name,
    id,
    label,
    disabled,
    readOnly,
    labelAddon,
    showErrorMessage,
    onValueChange,
    getOptionValue,
    getOptionLabel,
    onOpenChange,
    queryFn,
    ...dropdownProps
  } = props;

  const {
    field: { ref: _ref, value, onChange, onBlur },
    fieldState: { invalid },
  } = useController<TFormFields>({ name, disabled });

  const selectedItems = (value ?? []) as T[];

  return (
    <FormField
      name={name}
      id={id}
      label={label}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
      showErrorMessage={showErrorMessage}
    >
      <MultiSelect<T>
        value={selectedItems}
        onValueChange={(items) => {
          onChange(items);
          onBlur();
          onValueChange?.(items);
        }}
        getItemLabel={getOptionLabel}
        getItemValue={(item) => String(getOptionValue(item))}
        disabled={disabled || readOnly}
        renderDropdown={({
          onSelect,
          selectedValues,
          resetKey,
          disabled: isDisabled,
        }) => (
          <DropdownSingleSelectAsync<T, K>
            key={resetKey}
            id={id || name}
            {...dropdownProps}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            queryFn={async (search) => {
              const results = await queryFn(search);

              return results.filter(
                (item) => !selectedValues.has(String(getOptionValue(item))),
              );
            }}
            onChange={onSelect}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                onBlur();
              }

              onOpenChange?.(isOpen);
            }}
            state={invalid ? 'error' : 'default'}
            disabled={isDisabled}
          />
        )}
      />
    </FormField>
  );
}

FormFieldDropdownMultiSelectAsync.displayName =
  'FormFieldDropdownMultiSelectAsync';
